/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// redux
import { useSelector, useDispatch } from 'react-redux';
// react-router
import { useLocation } from 'react-router-dom'
// moment.js
import moment from "moment";
// react plugin for creating notifications over the dashboard
import NotificationAlert from "react-notification-alert";
// react component used to create sweet alerts
import ReactBSAlert from "react-bootstrap-sweetalert";

// reactstrap components
import {
  Table,
  Card,
  CardHeader,
  Button,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Modal,
} from "reactstrap";

// core components
import UserHeader from "components/Headers/UserHeader.js";
import Calendar from '../calendar/Calendar'
import ProInvitation from 'components/Misc/ProInvitation'
import Loading from 'components/Misc/Loading'

// actions
import { addUserLocation, removeUserLocation, getPastChecklists, createUserCalendarEvents } from 'actions/checklistActions';

function User() {
  const checklistsState = useSelector(state => state.checklists);
  const userState = useSelector(state => state.user);
  const [user, setUser] = React.useState(null);
  const [userModal, setUserModal] = React.useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const uid = location.search.split('=')[1]
  const [alert, setAlert] = React.useState(false);

  // Modal state

  // Focus state
  const [focusedFranchiseSelect, setFocusedFranchiseSelect] = React.useState(false);
  const [focusedLocationSelect, setFocusedLocationSelect] = React.useState(false);

  // Valid State
  const [validFranchiseSelect, setValidFranchiseSelect] = React.useState(false);
  const [validLocationSelect, setValidLocationSelect] = React.useState(false);

  // Content State
  const [franchiseSelect, setFranchiseSelect] = React.useState(false);
  const [locationSelect, setLocationSelect] = React.useState(false);

  const notificationAlertRef = React.useRef(null);


  const notify = (type, title, message, icon) => {
    let options = {
      place: "tc",
      message: (
        <div className="alert-text">
          <span className="alert-title" data-notify="title">
            {" "}
            {title}
          </span>
          <span data-notify="message">{message}</span>
        </div>
      ),
      type: type,
      icon,
      autoDismiss: 7,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const warningAlert = (location) => {
    setAlert(
      <ReactBSAlert
        warning
        showCancel
        style={{ display: "block", marginTop: "-100px" }}
        title="Warning"
        onConfirm={() => {
          handleLocationDelete(location);
          setAlert(null);
        }}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="warning"
        confirmBtnText="Yes, unassign it!"
        cancelBtnBsStyle="primary"
        cancelBtnText="Cancel"
        btnSize=""
      >
        You are about remove {user.name} from {location.replace('%', ' | ')}. The statistics will not change but they will no longer be able to complete checklists for this location.
      </ReactBSAlert>
    );
  };

  const handleAssignLocation = async (e) => {
    e.preventDefault();
    try {
      if (
        validFranchiseSelect === 'valid' &&
        validLocationSelect === 'valid'
      ) {
        const userLocations = Object.keys(user.locationStatistics);
        userLocations.push(`${franchiseSelect}%${locationSelect}`);
        if (user.technician) {
          await dispatch(addUserLocation({
            uid,
            franchise: franchiseSelect,
            location: locationSelect,
            locations: userLocations,
            technician: user.technician,
          }))
        } else {
          const locationManagers = checklistsState.franchises[franchiseSelect].locations.filter(location => location.address === locationSelect)[0].managers;
          !locationManagers ? locationManagers = [uid] : locationManagers.push(uid);
          await dispatch(addUserLocation({
            uid,
            franchise: franchiseSelect,
            location: locationSelect,
            locations: userLocations,
            technician: user.technician,
            locationManagers,
          }))
        }
        createUserState();
        setUserModal(false);
        clearState()
        return notify(
          "success",
          "Success!",
          `${franchiseSelect} | ${locationSelect} was assigned successfully to ${user.name}`,
          "fas fa-check"
        );
      }
      let message = '';
      if (!validFranchiseSelect || validFranchiseSelect === 'valid') {
        message += 'Please select a Franchise.\n'
      }
      if (!validLocationSelect || validLocationSelect === 'valid') {
        message += 'Please select a location.\n'
      }
      return notify(
        "danger",
        "Please make sure everything is filled out correctly:",
        message,
        "fas fa-exclamation-triangle"
      );
      //Notify if invalid
    } catch(error) {
      console.error(error);
      setUserModal(false);
      clearState()
      return notify(
        "danger",
        "Uh Oh!",
        error.message,
        "fas fa-exclamation-triangle"
      );
    }
  }

  const handleLocationDelete = async (location) => {
    try {
      const userLocations = Object.keys(user.locationStatistics).filter(userLocation => userLocation != location);
      const franchise = location.split('%')[0];
      const address = location.split('%')[1];
      if (user.technician) {
        await dispatch(removeUserLocation({
          uid,
          franchise,
          location: address,
          locations: userLocations,
          technician: user.technician,
        }))
      } else {
        const locationManagers = checklistsState.franchises[franchise].locations.filter(location => location.address === address)[0].managers.filter(manager => manager !== uid);
        await dispatch(removeUserLocation({
          uid,
          franchise,
          location: address,
          locations: userLocations,
          technician: user.technician,
          locationManagers,
        }))
      }
      createUserState();
      return notify(
        "success",
        "Success!",
        `${user.name} was removed successfully from ${location.replace('%', ' | ')}`,
        "fas fa-check"
      );
    } catch(error) {
      console.error(error);
      setUserModal(false);
      clearState()
      return notify(
        "danger",
        "Uh Oh!",
        error.message,
        "fas fa-exclamation-triangle"
      );
    }
  }

  const clearState = () => {
    setValidFranchiseSelect(null);
    setValidLocationSelect(null);
    setFranchiseSelect(null);
    setLocationSelect(null);
  }

  const createCalendar = () => {
    const calendar = [];
    const locationStatistics = {};
    checklistsState.users[uid].assignedLocations.forEach(location => {
      const data = {
        ...checklistsState.pastChecklists[location],
      };
      if (!locationStatistics[location]) {
        locationStatistics[location] = {
          totalChecklists: 0,
          onTime: 0,
          notOnTime: 0,
          notCompleted: 0,
        }
      }
      if (data) {
        Object.keys(data).forEach(date => {
          delete data[date]["date"];
          Object.keys(data[date]).forEach((checklist, index) => {
            const currentChecklist = data[date][checklist];
            if (currentChecklist.completedBy !== uid) return;
            const dayStatistics = {
              totalChecklists: 0,
              completedOnTime: 0,
            }
            locationStatistics[location].totalChecklists += 1;
            dayStatistics.totalChecklists += 1;
            if (currentChecklist.onTime) {
              locationStatistics[location].onTime += 1;
              dayStatistics.completedOnTime += 1;
            } else if (currentChecklist.completionTime !== '') {
              locationStatistics[location].notOnTime += 1;
            } else {
              locationStatistics[location].notCompleted += 1;
            }
            calendar.push({
              id: date + checklist + index,
              title: location.split('%').join(' | ') + ': ' + checklist,
              start: moment(date)._d,
              allDay: true,
              description: `${
                currentChecklist.completionTime
                  ? `This checklist was completed by ${
                      checklistsState.users[currentChecklist.completedBy].name
                    } @ ${currentChecklist.completionTime}. It was completed ${
                      currentChecklist.onTime ? "on time" : "late"
                    } and it ${
                      currentChecklist.passed ? "passed" : "didn't pass"
                    }.`
                  : "This checklist was not completed"
              }.`,
              className: currentChecklist.completionTime
                ? currentChecklist.onTime && currentChecklist.passed
                  ? "bg-success"
                  : "bg-yellow"
                : "bg-danger",
            });
          });
        });
      }
    })
    dispatch(createUserCalendarEvents(uid));
    setUser({
      name: checklistsState.users[uid].name,
      locationStatistics,
      calendar,
      technician: checklistsState.users[uid].technician,
    })
  }

  const createUserState = async () => {
    try {
      if (((userState.user.role === 'owner' && userState.user.plan !== 'free') || userState.user.role !== 'owner')) {
        const checklistsToFetch = [];
        checklistsState.users[uid].assignedLocations.forEach(location => {
          if (!checklistsState.pastChecklists || !checklistsState.pastChecklists[location]) {
            checklistsToFetch.push(location)
          }
        })
        if (checklistsToFetch.length > 0) return await Promise.all(checklistsToFetch.map(async location => await dispatch(getPastChecklists(null, location.split('%')[0], location.split('%')[1]))))
        createCalendar();
      }
    } catch(error) {
      throw new Error(`Error building user calendar: ${error}`);
    }
  }

  React.useEffect(() => {
    if (checklistsState.pastChecklists) {
      createCalendar();
    }
  }, [checklistsState.pastChecklists])

  React.useEffect(() => {
    createUserState();
  }, [location.search.split('=')[1]])

  if (userState.user.role === 'owner' && userState.user.plan === 'free') {
    return (
      <ProInvitation proFeature={'user'}/>
    )
  }

  if (!user) {
    return (
      <Loading active={true} />
    )
  }

  return (
    <>
      {alert}
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Modal
        isOpen={userModal}
        toggle={() => {
          setUserModal(false);
          clearState();
        }}
        className="modal-dialog-centered modal-secondary"
        style={{ width: "90%", maxWidth: "1200px" }}
      >
        <div className="modal-body">
          <Row>
            <div className="col">
              <Card className="shadow px-6">
                <CardHeader className="bg-transparent border-0">
                  <h3 className="mb-0">
                    Assign a location to {user.name}
                  </h3>
                  {user && user.technician ? <h4 className="text-danger">WARNING: If you assign technician {user.name} to another location the technician assigned to that location will be replaced.</h4> : null}
                </CardHeader>
                <Form
                  className="edit-event--form pt-3"
                  id="form"
                  onSubmit={e => handleAssignLocation(e)}
                >
                  <FormGroup>
                    <Input
                      id="3"
                      type="select"
                      default="1"
                      valid={validFranchiseSelect === "valid"}
                      invalid={validFranchiseSelect === "invalid"}
                      onFocus={() => setFocusedFranchiseSelect(true)}
                      onBlur={() => setFocusedFranchiseSelect(false)}
                      onChange={e => {
                        setFranchiseSelect(e.target.value);
                        if (e.target.value === "") {
                          setValidFranchiseSelect("invalid");
                        } else {
                          setValidFranchiseSelect("valid");
                        }
                      }}
                    >
                      <option className="d-none" value="1">
                        Select the franchise {user.name} will be working at.
                      </option>
                      {Object.keys(checklistsState.franchises).map(
                        franchise => (
                          <option key={franchise}>{franchise}</option>
                        )
                      )}
                    </Input>
                  </FormGroup>
                  {
                    franchiseSelect ? (
                      <FormGroup>
                        <Input
                          id="3"
                          type="select"
                          default="1"
                          valid={validLocationSelect === "valid"}
                          invalid={validLocationSelect === "invalid"}
                          onFocus={() => setFocusedLocationSelect(true)}
                          onBlur={() => setFocusedLocationSelect(false)}
                          onChange={e => {
                            setLocationSelect(e.target.value);
                            if (e.target.value === "") {
                              setValidLocationSelect("invalid");
                            } else {
                              setValidLocationSelect("valid");
                            }
                          }}
                        >
                          <option className="d-none" value="1">
                            Select the Location.
                          </option>
                          {checklistsState.franchises[franchiseSelect].locations.map(
                            location => (
                              <option key={location.address}>{location.address}</option>
                            )
                          )}
                        </Input>
                      </FormGroup>
                        
                    ) :
                    null
                  }
                </Form>
              </Card>
            </div>
          </Row>
        </div>
        <div className="modal-footer">
          <Button color="primary" type="button" type="submit" form="form">
            Assign location
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setUserModal(false);
              clearState();
            }}
          >
            Close
          </Button>
        </div>
      </Modal>
      <UserHeader name={`${user.name} : ${user.technician ? 'Technician' : 'Manager'}`} statistics={user.locationStatistics} parentName="Pages" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="bg-default shadow">
              <CardHeader className="border-0 bg-transparent text-white d-flex">
                <h3 className="text-white mb-0">Assigned Locations</h3>
                <Button color="primary" outline type="button" className="ml-auto" onClick={() => setUserModal(true)}>
                  Add Location
                </Button>
              </CardHeader>
              <Table
                className="align-items-center table-dark table-flush"
                responsive
              >
                <thead className="thead-dark">
                  <tr>
                    <th
                      className="sort text-center"
                      data-sort="location"
                      scope="col"
                    >
                      Franchise
                    </th>
                    <th
                      className="sort text-center"
                      data-sort="location"
                      scope="col"
                    >
                      Address
                    </th>
                    <th
                      className="sort text-center"
                      data-sort="plan"
                      scope="col"
                    >
                      Total Checklists
                    </th>
                    <th
                      className="sort text-center"
                      data-sort="paymentDate"
                      scope="col"
                    >
                      Completed in Time
                    </th>
                    <th
                      className="sort text-center"
                      data-sort="expirationDate"
                      scope="col"
                    >
                      Completed out of time
                    </th>
                    <th
                      className="sort text-center"
                      data-sort="expirationDate"
                      scope="col"
                    >
                      Not Completed
                    </th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody className="list">
                  {Object.keys(user.locationStatistics).map(location => (
                    <tr key={location.split('%')[1]}>
                      <th scope="row">
                        <span className="location mb-0 text-sm">{location.split('%')[0]}</span>
                      </th>
                      <th scope="row">
                        <span className="location mb-0 text-sm">{location.split('%')[1]}</span>
                      </th>
                      <td className="text-center">{user.locationStatistics[location].totalChecklists}</td>
                      <td className="text-center">{user.locationStatistics[location].onTime}</td>
                      <td className="text-center">{user.locationStatistics[location].notOnTime}</td>
                      <td className="text-center">{user.locationStatistics[location].notCompleted}</td>
                      <td className="text-center">
                        <i
                          className="far fa-trash-alt text-danger"
                          style={{ cursor: "pointer" }}
                          onClick={() => warningAlert(location)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </div>
        </Row>
        <Row>
          <Col xl="12">
            <Calendar eventsVariables={user.calendar} locations={checklistsState.users[uid].assignedLocations} uid={uid} key={user.name}/>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default User;
